import React, { useState } from 'react';
import { router } from '@inertiajs/react';
import { X } from 'lucide-react';
import { AdminLayout } from '../../../design-system/components/layouts';
import {
  PageHeader,
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  CardBody,
  ConfirmDialog,
} from '../../../design-system/components';
import {
  admin_fraud_root_path,
  admin_fraud_rules_path,
  admin_fraud_reserved_words_path,
  admin_fraud_reserved_word_path,
} from '../../../lib/routes';
export default function ReservedWords({ words }) {
  const [newWord, setNewWord] = useState('');
  const [adding, setAdding] = useState(false);
  const [removingWord, setRemovingWord] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const filteredWords = searchFilter
    ? words.filter((w) => w.word.includes(searchFilter.toLowerCase()))
    : words;
  function handleAdd(e) {
    e.preventDefault();
    if (!newWord.trim()) return;
    setAdding(true);
    router.post(
      admin_fraud_reserved_words_path(),
      {
        words: newWord.trim().toLowerCase(),
      },
      {
        preserveScroll: true,
        onFinish: () => {
          setAdding(false);
          setNewWord('');
        },
      },
    );
  }
  function handleRemove() {
    if (!removingWord) return;
    router.delete(admin_fraud_reserved_word_path(removingWord.id), {
      preserveScroll: true,
      onFinish: () => setRemovingWord(null),
    });
  }
  return (
    <AdminLayout>
      <PageHeader
        title="Reserved Words"
        description={`${words.length} reserved word${words.length !== 1 ? 's' : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_rules_path())}
            >
              Rules
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.visit(admin_fraud_root_path())}
            >
              Back to Events
            </Button>
          </div>
        }
      />

      <div className="mt-6 space-y-6">
        <Card>
          <CardHeader>
            <h3 className="text-sm font-semibold text-fg">Add Words</h3>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-fg-tertiary mb-4">
              Reserved words are blocked from being used as affiliate slugs or coupon codes. Add words here to prevent affiliates from choosing inappropriate, misleading, or brand-conflicting identifiers.
            </p>
            <form onSubmit={handleAdd} className="space-y-3">
              <Textarea
                label="Words"
                placeholder="Enter words separated by commas or new lines"
                helperText="Separate multiple words with commas or new lines"
                value={newWord}
                onChange={(e) => setNewWord(e.target.value)}
                rows={3}
              />
              <div className="flex justify-end">
                <Button type="submit" size="sm" loading={adding}>
                  Add
                </Button>
              </div>
            </form>
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-fg">
                Reserved Words ({filteredWords.length})
              </h3>
              {words.length > 10 && (
                <div className="w-64">
                  <Input
                    placeholder="Filter words..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                  />
                </div>
              )}
            </div>
          </CardHeader>
          <CardBody>
            {filteredWords.length === 0 ? (
              <p className="text-sm text-fg-tertiary">
                {searchFilter ? 'No words match your filter.' : 'No reserved words yet.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {filteredWords.map((w) => (
                  <div
                    key={w.id}
                    className="flex items-center justify-between rounded-md border border-edge-strong px-3 py-2"
                  >
                    <span className="text-sm text-fg-secondary font-mono">{w.word}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-2 !p-0.5 text-fg-tertiary hover:text-danger-500"
                      onClick={() => setRemovingWord(w)}
                      title="Remove word"
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={removingWord !== null}
        onClose={() => setRemovingWord(null)}
        onConfirm={handleRemove}
        title="Remove Reserved Word"
        message={`Are you sure you want to remove "${removingWord?.word}" from reserved words?`}
        confirmLabel="Remove"
        destructive
      />
    </AdminLayout>
  );
}
